import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { createRef, useCallback, useState } from 'react';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import PropertyInputEmailV2 from '../../PropertyInputEmailV2';
import UIEditableControls from 'UIComponents/editable/UIEditableControls';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { usePropertyValidation } from '../../../validation/hooks/usePropertyValidation';
import emptyFunction from 'react-utils/emptyFunction';
import { denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import UIFormControl from 'UIComponents/form/UIFormControl';

const SingleEmailInputWithControls = ({
  accessLevel,
  className,
  disabled,
  isNewEmail,
  isOnlyEmail,
  isPrimaryEmail,
  objectTypeId,
  onDelete,
  onPromoteToPrimary,
  onSave,
  property,
  subjectId,
  value,
  showAutoSaveText
}) => {
  // We use a draftValue of undefined when no changes are being made because the
  // source of truth for value is the "value" prop. The draft is only used as
  // the value when the user is editing the email, as soon as they save or
  // cancel we set it back to undefined.
  const [draftValue, setDraftValue] = useState(undefined);
  const currentValue = draftValue === undefined ? value : draftValue;
  const handleDraftChange = useCallback(changes => {
    const newEmailValue = changes[property.name];

    if (newEmailValue != null) {
      setDraftValue(Object.assign({}, currentValue, {
        value: newEmailValue
      }));
    }
  }, [currentValue, property.name]);
  const {
    onChangeSingle,
    onChangeMultiple,
    validationState: {
      saveable,
      invalid,
      message
    }
  } = usePropertyValidation({
    objectId: subjectId,
    objectTypeId,
    onChange: handleDraftChange,
    // No `onValidationChange` needed because we don't broadcast the result of
    // this validation to our parent component. Validation is entirely internal
    // to this component
    onValidationChange: emptyFunction,
    propertyDefinition: property,
    propertyValue: currentValue.value
  });
  const inputRef = /*#__PURE__*/createRef();
  const handleDelete = useCallback(() => {
    onDelete(currentValue);
  }, [onDelete, currentValue]);
  const handlePromoteToPrimary = useCallback(() => {
    onPromoteToPrimary(currentValue);
  }, [currentValue, onPromoteToPrimary]);
  const handleSave = useCallback(() => {
    onSave(draftValue, value);
    setDraftValue(undefined);
  }, [draftValue, onSave, value]);
  const handleCancel = useCallback(() => {
    if (isNewEmail) {
      onDelete(currentValue);
    } else {
      setDraftValue(undefined);
    }
  }, [currentValue, isNewEmail, onDelete]);
  const isDeleteButtonDisabled = isPrimaryEmail && !isOnlyEmail || disabled;
  const isMakePrimaryButtonDisabled = draftValue !== undefined || disabled;
  const confirmLabel = showAutoSaveText ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputMultipleEmailV2.save"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputMultipleEmailV2.apply"
  });
  return /*#__PURE__*/_jsxs(UIFlex, {
    className: className,
    wrap: "wrap",
    children: [/*#__PURE__*/_jsx(UIBox, {
      grow: 1,
      className: "m-right-4 m-y-1",
      children: /*#__PURE__*/_jsx(UIEditableControls, {
        className: "m-all-0",
        onCancel: handleCancel,
        onSave: handleSave,
        saveDisabled: !saveable || draftValue === undefined || draftValue.value === '',
        use: "flush",
        saveLabel: confirmLabel,
        children: /*#__PURE__*/_jsx(UIFormControl, {
          error: invalid,
          validationMessage: message,
          children: /*#__PURE__*/_jsx(PropertyInputEmailV2, {
            accessLevel: accessLevel,
            disabled: disabled,
            inputRef: inputRef,
            internalValue: value.value,
            objectType: denormalizeTypeId(objectTypeId),
            objectTypeId: objectTypeId,
            onChange: onChangeSingle,
            onChangeMultiple: onChangeMultiple,
            onValidationChange: emptyFunction,
            popoverOpen: false,
            setPopoverOpen: emptyFunction,
            property: property,
            subjectId: subjectId,
            value: currentValue.value,
            formControlUse: "default"
          })
        })
      })
    }), !isNewEmail && /*#__PURE__*/_jsx(UIBox, {
      alignSelf: "start",
      shrink: 0,
      className: "m-y-1",
      children: /*#__PURE__*/_jsxs(UIButtonWrapper, {
        children: [!isPrimaryEmail && /*#__PURE__*/_jsx(UITooltip, {
          "data-test-id": "multiple-email-make-primary-tooltip",
          disabled: !isMakePrimaryButtonDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputMultipleEmailV2.makePrimary.disabledTooltip"
          }),
          children: /*#__PURE__*/_jsx(UIButton, {
            "data-test-id": "multiple-email-make-primary-btn",
            disabled: isMakePrimaryButtonDisabled,
            onClick: handlePromoteToPrimary,
            use: "tertiary-light",
            size: "sm",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputMultipleEmailV2.makePrimary.buttonText"
            })
          })
        }), /*#__PURE__*/_jsx(UITooltip, {
          "data-test-id": "multiple-email-delete-tooltip",
          disabled: !isDeleteButtonDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.disabledTooltip"
          }),
          children: /*#__PURE__*/_jsx(UIButton, {
            "data-test-id": "multiple-email-delete-btn",
            disabled: isDeleteButtonDisabled,
            onClick: handleDelete,
            use: "danger",
            size: "sm",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.buttonText"
            })
          })
        })]
      })
    })]
  });
};

export default SingleEmailInputWithControls;